import React, { useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { LngLatLike, Map as MaplibreMap } from 'maplibre-gl'
import { MapMarker } from '../types'
import { createMaplibreMarker } from '../utils'

export type CustomMarkerProps = {
  componentType: React.ElementType
  componentProps: MapMarker<unknown>
  map: MaplibreMap
  coordinates: LngLatLike
  classList?: string[]
  onMarkerClick?: (marker: MapMarker<unknown>) => void
}

/**
 * Component used to display a custom HTML marker on the map.
 */
export const CustomMarker = ({
  componentType: ComponentType,
  componentProps,
  map,
  coordinates,
  classList,
  onMarkerClick,
}: CustomMarkerProps) => {
  const [domElement, setHtmlElement] = useState<HTMLDivElement | null>()

  const handleClickInternal = useCallback(
    function (this: HTMLElement, ev: MouseEvent) {
      onMarkerClick?.(componentProps)
    },
    [componentProps, onMarkerClick]
  )

  useEffect(() => {
    // Only create the marker when both the map and the dom element are ready.
    if (map && domElement) {
      const markerEl = createMaplibreMarker(domElement, coordinates, map, handleClickInternal, classList)

      return () => {
        markerEl?.remove()
      }
    }

    // Just to shut TypeScript up about not all code paths returning a value.
    return
  }, [classList, coordinates, domElement, handleClickInternal, map, onMarkerClick])

  return (
    <div className="hidden">
      <div ref={(ref) => setHtmlElement(ref)} />
      {domElement ? ReactDOM.createPortal(<ComponentType {...componentProps} />, domElement) : null}
    </div>
  )
}
